<script setup>
import {reactive, ref, watch, nextTick} from "vue";
import AiChatLoader from "@/components/aiChat/AiChatLoader.vue";
import useAiChat from "@/composables/useAiChat";
import needsService from "@/services/needs.service";
import Matching from "@/views/needs/chat/Matching.vue";
import {stripPaste} from "@/helpers/string";

const showModal = ref(false);
const content = ref(null);
const matching = ref(null);
const showResult = ref(false);
const showMatching = ref(false);

const roundFormat = {
    question: '',
    answer: '',
    output: null
};

const params = {
    url: '/chat-stream/get-needs-ai-response',
    roundFormat,
    content,
    addEvents: {
        'finished': finish
    }
}

const {
    question,
    rounds,
    loading,
    loaderMessage,
    clearStream,
    sendRequest,
    sanitizeAnswer,
    reset,
} = useAiChat(params);

const need = reactive({
    name: '',
    description: '',
    conversationId: 0
});

// ensure we always close stream when modal is hidden or component unmounts
watch(showModal, (value) => {
    if (!value) {
        clearStream();
    }
});

function finish(message) {
    const needData = JSON.parse(message);

    need.name = needData.name;
    need.description = needData.description;
    need.conversationId = needData.conversation_id;

    showResult.value = true;
}

async function createNeed() {
    const {data: needId} = await needsService.createNeed(need);

    showMatching.value = true;

    await nextTick();
    matching.value.start(needId);
}

function open() {
    reset();
    showModal.value = true;
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" hide-footer hide-header modal-class="modal-ai-chat">
        <!-- <a href="#" class="close">Back</a> -->
        <section class="section-ai-chat" ref="content">
            <button @click="showModal = false" type="button" aria-label="Close" class="close"></button>

            <div class="ai-chat__inner">
                <Matching
                    v-if="showMatching"
                    ref="matching"
                />
                <template v-else>
                    <div class="ai-chat__content">
                        <div v-if="!rounds.length">
                            <h2>What need are you exploring?</h2>
                            <p>Describe a business challenge-Al and our ecosystem partners will help you find solutions.</p>

                            <div class="card--ai__search">
                            <span
                                role="textbox"
                                contenteditable
                                v-focus
                                placeholder="Describe your business challenge..."
                                class="card--ai__search__input"
                                @paste.prevent="stripPaste"
                                @input="question = $event.target.textContent"
                                @keyup.enter.prevent="sendRequest"
                            />

                                <button class="card--ai__search__button" @click="sendRequest"></button>
                            </div>
                        </div>

                        <template v-else >
                            <div class="ai-chat">
                                <div
                                    v-for="(round, index) in rounds"
                                    :key="index"
                                    class="ai-chat__inner"
                                >
                                    <div class="ai-chat__user">
                                        {{round.question}}
                                    </div>

                                    <div class="ai-chat__machine" :class="{'long-div': index === rounds.length - 1}">
                                        <div v-if="loaderMessage.length && index === rounds.length -1">
                                            <AiChatLoader :message="loaderMessage"/>
                                        </div>

                                        <p v-html="sanitizeAnswer(round.answer)"/>
                                    </div>
                                </div>
                            </div>

                            <div v-if="!loading && !showResult" class="ai-chat__footer">
                                <div class="card--ai__search">
                            <span
                                role="textbox"
                                contenteditable
                                v-focus
                                placeholder="Ask about startups or partnerships"
                                class="card--ai__search__input"
                                @paste.prevent="stripPaste"
                                @input="question = $event.target.textContent"
                                @keyup.enter.prevent="sendRequest"
                            />

                                    <button class="card--ai__search__button" @click="sendRequest"></button>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div v-if="showResult">
                        <p>Here's a summary of your need. Review and edit these fields before creating it.</p>

                        <div class="mb-5">
                            <h5 class="heading5 heading5--secondary">Title</h5>
                            <input type="text" class="form-control form-control--light" v-model="need.name"/>
                        </div>

                        <div class="mb-5">
                            <h5 class="heading5 heading5--secondary">Description</h5>
                            <textarea class="form-control form-control--light form-control--lg" v-model="need.description"/>
                        </div>

                        <div class="pt-5 text-right">
                            <a class="btn btn-primary btn-lg" @click="createNeed">Create Need</a>
                        </div>
                    </div>
                </template>
            </div>
        </section>
    </b-modal>
</template>

<style scoped>
.long-div {
    min-height: 140px;
}

.section-ai-chat {
    overflow-y: auto;
    scroll-behavior: smooth;
    padding-bottom: 160px;
}

.ai-chat__footer {
    pointer-events: none;
}

.ai-chat__footer .card--ai__search {
    pointer-events: auto;
}
</style>
